import { createGenericStore } from '@/stores/utils/genericStore.js'
import { useAuthStore } from '@/stores/auth.js'
import { numericId } from './chatStoreUtils.js'
import { useBrowserNotificationStore } from './browserNotificationStore.js'
import { useConversationParticipantStore } from './conversationParticipantStore.js'
import { useConversationStore } from './conversationStore.js'
import { useMessageStore } from './messageStore.js'
import { usePresenceUserStore } from './presenceUserStore.js'

const chatStoreOptions = {
  state: () => ({
    activeConversationId: null,
    globalPresenceChannelName: 'chat.online',
    isSubscribedToGlobalPresence: false,
    subscribedConversationIds: [],
    conversationChannelPrefix: 'chat.conversation',
    isSubscribing: false
  }),

  getters: {
    activeConversation(state) {
      return useConversationStore().getConversationById(state.activeConversationId)
    },
    activeMessages(state) {
      return useMessageStore().getMessagesByConversationId(state.activeConversationId)
    },
    isSubscribedToConversation(state) {
      return (conversationId) => state.subscribedConversationIds.includes(numericId(conversationId))
    }
  },

  actions: {
    setActiveConversation(conversationId) {
      this.activeConversationId = conversationId === null ? null : numericId(conversationId)
    },

    clearActiveConversation() {
      this.activeConversationId = null
    },

    subscribeToGlobalPresence() {
      if (!window.Echo || this.isSubscribedToGlobalPresence) {
        return
      }

      const presenceUserStore = usePresenceUserStore()

      window.Echo.join(this.globalPresenceChannelName)
        .here((users) => presenceUserStore.setGlobalUsers(users))
        .joining((user) => presenceUserStore.markGlobalUserOnline(user))
        .leaving((user) => presenceUserStore.markGlobalUserOffline(user))
        .listen('.Umisis\\Chat\\Events\\UserStatusUpdatedEvent', (event) => this.handleUserStatusUpdatedEvent(event))

      this.isSubscribedToGlobalPresence = true
    },

    unsubscribeFromGlobalPresence() {
      if (window.Echo) {
        window.Echo.leave(this.globalPresenceChannelName)
      }

      usePresenceUserStore().clearGlobalPresence()
      this.isSubscribedToGlobalPresence = false
    },

    subscribeToConversation(conversationId) {
      const normalizedConversationId = numericId(conversationId)

      if (!window.Echo || this.subscribedConversationIds.includes(normalizedConversationId)) {
        return
      }

      this.isSubscribing = true
      const channelName = `${this.conversationChannelPrefix}.${normalizedConversationId}`
      const presenceUserStore = usePresenceUserStore()

      window.Echo.join(channelName)
        .here((users) => presenceUserStore.setConversationUsers(normalizedConversationId, users))
        .joining((user) => presenceUserStore.markUserOnline(normalizedConversationId, user))
        .leaving((user) => presenceUserStore.markUserOffline(normalizedConversationId, user))
        .listen('.Umisis\\Chat\\Events\\MessageSentEvent', (event) => this.handleMessageSentEvent(event))
        .listen('.Umisis\\Chat\\Events\\MessageUpdatedEvent', (event) => this.handleMessageUpdatedEvent(event))
        .listen('.Umisis\\Chat\\Events\\MessageDeletedEvent', (event) => this.handleMessageDeletedEvent(event))
        .listen('.Umisis\\Chat\\Events\\MessageReactionUpdatedEvent', (event) => this.handleMessageReactionUpdatedEvent(event))
        .listen('.Umisis\\Chat\\Events\\ConversationReadEvent', (event) => this.handleConversationReadEvent(event))
        .listen('.Umisis\\Chat\\Events\\ConversationUpdatedEvent', (event) => this.handleConversationUpdatedEvent(event))
        .listen('.Umisis\\Chat\\Events\\ConversationParticipantUpdatedEvent', (event) => this.handleConversationParticipantUpdatedEvent(event))

      this.subscribedConversationIds = [...this.subscribedConversationIds, normalizedConversationId]
      this.isSubscribing = false
    },

    subscribeToConversations(conversationIds = null) {
      const ids = conversationIds ?? useConversationStore().items.map((conversation) => conversation.id)

      for (const conversationId of ids) {
        this.subscribeToConversation(conversationId)
      }
    },

    unsubscribeFromConversation(conversationId) {
      const normalizedConversationId = numericId(conversationId)

      if (window.Echo) {
        window.Echo.leave(`${this.conversationChannelPrefix}.${normalizedConversationId}`)
      }

      usePresenceUserStore().clearConversationPresence(normalizedConversationId)
      this.subscribedConversationIds = this.subscribedConversationIds.filter((id) => id !== normalizedConversationId)
    },

    unsubscribeFromAllConversations() {
      for (const conversationId of [...this.subscribedConversationIds]) {
        this.unsubscribeFromConversation(conversationId)
      }
    },

    handleMessageSentEvent(event) {
      if (!event?.message) {
        return
      }

      const messageStore = useMessageStore()
      const isNewMessage = !messageStore.getMessagesByConversationId(event.message.conversation_id)
        .some((message) => numericId(message.id) === numericId(event.message.id))
      const receivedMessage = {
        ...event.message,
        delivery_status: 'received'
      }

      messageStore.patchMessageState(event.message.conversation_id, receivedMessage)
      this.patchConversationForReceivedMessage(receivedMessage, isNewMessage)
      this.notifyForIncomingMessage(event.message)
    },

    patchConversationForReceivedMessage(message, isNewMessage) {
      const conversationStore = useConversationStore()
      let conversation = conversationStore.getConversationById(message.conversation_id)

      if (!conversation) {
        return
      }

      const currentUserId = useAuthStore().user?.id
      const isIncomingMessage = currentUserId !== null
        && currentUserId !== undefined
        && numericId(message.sender_user_id) !== numericId(currentUserId)

      if (isNewMessage && isIncomingMessage && conversation.current_participant) {
        useConversationParticipantStore().patchConversationParticipantState(message.conversation_id, {
          ...conversation.current_participant,
          unread_count: Number(conversation.current_participant.unread_count || 0) + 1
        })
        conversation = conversationStore.getConversationById(message.conversation_id)
      }

      const shouldUpdateLastMessage = !conversation.last_message_id
        || numericId(message.id) >= numericId(conversation.last_message_id)

      if (!shouldUpdateLastMessage) {
        return
      }

      conversationStore.patchConversationState({
        ...conversation,
        last_message_id: message.id,
        last_message_at: message.created_at,
        last_message: message
      })
    },

    notifyForIncomingMessage(message) {
      const currentUserId = useAuthStore().user?.id

      if (currentUserId !== null && currentUserId !== undefined && numericId(message.sender_user_id) === numericId(currentUserId)) {
        return
      }

      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && numericId(this.activeConversationId) === numericId(message.conversation_id)) {
        return
      }

      const conversation = useConversationStore().getConversationById(message.conversation_id)

      useBrowserNotificationStore().notifyMessage(message, conversation)
    },

    handleMessageUpdatedEvent(event) {
      if (!event?.message) {
        return
      }

      useMessageStore().patchMessageState(event.message.conversation_id, event.message)
    },

    handleMessageDeletedEvent(event) {
      if (!event?.conversation_id || !event?.message_id) {
        return
      }

      useMessageStore().removeMessageFromState(event.conversation_id, event.message_id)
    },

    handleMessageReactionUpdatedEvent(event) {
      if (!event?.conversation_id || !event?.message_id || !event?.reaction) {
        return
      }

      useMessageStore().patchMessageReactionState(event.conversation_id, event.message_id, event.reaction, event.action)
    },

    handleConversationReadEvent(event) {
      if (!event?.conversation_id || !event?.user_id) {
        return
      }

      const conversation = useConversationStore().getConversationById(event.conversation_id)
      const participant = conversation?.participants?.find((item) => numericId(item.user_id) === numericId(event.user_id))

      if (!participant) {
        return
      }

      useConversationParticipantStore().patchConversationParticipantState(event.conversation_id, {
        ...participant,
        last_read_message_id: event.last_read_message_id,
        last_read_at: event.last_read_at,
        unread_count: event.unread_count
      })
    },

    handleConversationUpdatedEvent(event) {
      if (!event?.conversation) {
        return
      }

      const conversationStore = useConversationStore()
      const existingConversation = conversationStore.getConversationById(event.conversation.id) || {}

      conversationStore.patchConversationState({
        ...existingConversation,
        ...event.conversation
      })
    },

    handleConversationParticipantUpdatedEvent(event) {
      if (!event?.conversation_id || !event?.participant) {
        return
      }

      useConversationParticipantStore().patchConversationParticipantState(event.conversation_id, event.participant)
    },

    handleUserStatusUpdatedEvent(event) {
      if (!event?.user_status?.user_id) {
        return
      }

      usePresenceUserStore().patchUserState({
        id: event.user_status.user_id,
        chat_status: event.user_status.status,
        chat_status_label: event.user_status.label
      })
    }
  }
}

export const useChatStore = createGenericStore(
  'chat',
  '/api/v1/conversations',
  chatStoreOptions
)
